import { PlayerCard } from "./PlayerCard";

interface Player {
  id: string;
  name: string;
  team: string;
  position: string;
  photo_url?: string;
  is_active: boolean;
}

interface RosterSlot {
  id: string;
  player_id: string;
  position: string;
  slot_type: string;
  player?: Player;
}

interface FormationGridProps {
  starters: RosterSlot[];
  captain: string | null;
  onCaptainToggle: (playerId: string) => void;
  canEdit: boolean;
}

export function FormationGrid({
  starters,
  captain,
  onCaptainToggle,
  canEdit,
}: FormationGridProps) {
  // Formation positions based on 4-3-3
  const formationPositions = {
    GK: [{ position: 'GK', gridPos: 'row-4 col-3' }],
    DEF: [
      { position: 'DEF', gridPos: 'row-3 col-1' },
      { position: 'DEF', gridPos: 'row-3 col-2' },
      { position: 'DEF', gridPos: 'row-3 col-4' },
      { position: 'DEF', gridPos: 'row-3 col-5' },
    ],
    MID: [
      { position: 'MID', gridPos: 'row-2 col-2' },
      { position: 'MID', gridPos: 'row-2 col-3' },
      { position: 'MID', gridPos: 'row-2 col-4' },
    ],
    FWD: [
      { position: 'FWD', gridPos: 'row-1 col-1' },
      { position: 'FWD', gridPos: 'row-1 col-3' },
      { position: 'FWD', gridPos: 'row-1 col-5' },
    ],
  };

  const getPlayerForPosition = (position: string, index: number) => {
    const playersInPosition = starters.filter(
      slot => slot.position === position && slot.player
    );
    return playersInPosition[index] || null;
  };

  return (
    <div className="relative bg-gradient-to-t from-green-800/20 to-green-600/10 rounded-lg p-6 min-h-[400px]">
      {/* Football pitch styling */}
      <div className="absolute inset-0 bg-gradient-to-t from-green-700/10 to-transparent rounded-lg"></div>
      
      {/* Formation grid */}
      <div className="relative grid grid-cols-5 grid-rows-4 gap-4 h-full">
        {/* Forwards */}
        {formationPositions.FWD.map((pos, index) => {
          const slot = getPlayerForPosition('FWD', index);
          return (
            <div key={`fwd-${index}`} className={pos.gridPos}>
              {slot?.player ? (
                <PlayerCard
                  player={slot.player}
                  isCaptain={captain === slot.player.id}
                  onCaptainToggle={() => onCaptainToggle(slot.player!.id)}
                  showCaptainControl={canEdit}
                  size="medium"
                />
              ) : (
                <div className="aspect-[3/4] rounded-lg border-2 border-dashed border-white/30 bg-white/10 flex items-center justify-center text-xs text-white/70 text-center p-2">
                  Pick FWD
                </div>
              )}
            </div>
          );
        })}

        {/* Midfielders */}
        {formationPositions.MID.map((pos, index) => {
          const slot = getPlayerForPosition('MID', index);
          return (
            <div key={`mid-${index}`} className={pos.gridPos}>
              {slot?.player ? (
                <PlayerCard
                  player={slot.player}
                  isCaptain={captain === slot.player.id}
                  onCaptainToggle={() => onCaptainToggle(slot.player!.id)}
                  showCaptainControl={canEdit}
                  size="medium"
                />
              ) : (
                <div className="aspect-[3/4] rounded-lg border-2 border-dashed border-white/30 bg-white/10 flex items-center justify-center text-xs text-white/70 text-center p-2">
                  Pick MID
                </div>
              )}
            </div>
          );
        })}

        {/* Defenders */}
        {formationPositions.DEF.map((pos, index) => {
          const slot = getPlayerForPosition('DEF', index);
          return (
            <div key={`def-${index}`} className={pos.gridPos}>
              {slot?.player ? (
                <PlayerCard
                  player={slot.player}
                  isCaptain={captain === slot.player.id}
                  onCaptainToggle={() => onCaptainToggle(slot.player!.id)}
                  showCaptainControl={canEdit}
                  size="medium"
                />
              ) : (
                <div className="aspect-[3/4] rounded-lg border-2 border-dashed border-white/30 bg-white/10 flex items-center justify-center text-xs text-white/70 text-center p-2">
                  Pick DEF
                </div>
              )}
            </div>
          );
        })}

        {/* Goalkeeper */}
        {formationPositions.GK.map((pos, index) => {
          const slot = getPlayerForPosition('GK', index);
          return (
            <div key={`gk-${index}`} className={pos.gridPos}>
              {slot?.player ? (
                <PlayerCard
                  player={slot.player}
                  isCaptain={captain === slot.player.id}
                  onCaptainToggle={() => onCaptainToggle(slot.player!.id)}
                  showCaptainControl={canEdit}
                  size="medium"
                />
              ) : (
                <div className="aspect-[3/4] rounded-lg border-2 border-dashed border-white/30 bg-white/10 flex items-center justify-center text-xs text-white/70 text-center p-2">
                  Pick GK
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pitch markings */}
      <div className="absolute inset-x-6 top-6 h-px bg-white/20"></div>
      <div className="absolute inset-x-6 bottom-6 h-px bg-white/20"></div>
      <div className="absolute left-6 inset-y-6 w-px bg-white/20"></div>
      <div className="absolute right-6 inset-y-6 w-px bg-white/20"></div>
      
      {/* Center circle */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 border border-white/20 rounded-full"></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white/30 rounded-full"></div>
    </div>
  );
}
